import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { ArticleShareButton } from '$partials/articles';
import { ArticleStandardListingItem } from '$partials/listings/items';
import { Button } from '$partials/ui';
import { CarouselProvider } from '$contexts/carousel';
import { Footer, Header, Meta } from '$partials/global';
import { getBgColor } from '$utilities/data';
import { Profile } from '$partials/profiles';
import { useLocale } from '$contexts/locale.jsx';
import Carousel, { CarouselSlide } from '$partials/ui/carousel';
import Image from '$partials/media/image';
import ModuleDispatcher from '$partials/modules/dispatcher';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const AwardsArticlePage = ({ data, domain }) => {
    // misc
    const page = data?.page;

    // references
    const heroRef = useRef(null);

    // contexts
    const { current: locale, translate: t } = useLocale();

    // state
    const [ backgroundColourHeight, setBackgroundColourHeight ] = useState(0);

    // hooks
    useEffect(() => {
        if (heroRef.current) {
            setBackgroundColourHeight(heroRef.current.getBoundingClientRect().top);
        }
    }, [heroRef]);

    // useEffect(() => {
    //     const updateViewCount = async () => {
    //         try {
    //             const response = await fetch('/api/v1/viewCount', {
    //                 body: JSON.stringify({
    //                     'localeCode': locale.code,
    //                     'pageId': page.sys?.id,
    //                 }),
    //                 credentials: 'include',
    //                 headers: { 'Content-Type': 'application/json' },
    //                 method: 'POST'
    //             });

    //             const result = await response.json();

    //             if (result.status !== 200) {
    //                 throw new Error(result);
    //             }
    //         } catch (err) {
    //             console.error(err);
    //         }
    //     };

    //     updateViewCount();
    // }, []);

    // misc
    if (!page) { return null; }

    let bg = getBgColor(page.backgroundColor);

    const tags = [...page.topics?.items, ...page.activities?.items];

    // render
    return (
        <>
            <Meta
                page={page}
                type='Article' />

            <main
                className='bg-black min-h-screen'
                id='main-content'>
                {!!backgroundColourHeight && page.backgroundColor && (
                    <>
                        <div
                            className='w-screen absolute hidden sm:block'
                            style={{height: backgroundColourHeight + 100 + 'px', backgroundColor: bg}}/>

                        <div
                            className='w-screen absolute sm:hidden'
                            style={{height: backgroundColourHeight + 50 + 'px', backgroundColor: bg}}/>
                    </>
                )}

                <div className='layout-container z-20 relative'>
                    <Header
                        invertTextColour={true}
                        menu={data?.navigation}
                        search={data?.search}
                        title={page.title} />
                </div>

                <article className='relative z-10'>
                    {/* Header */}
                    <header className='layout-container'>
                        <div className='pt-60 sm:pt-75 md:pt-100'>
                            {/* Eyebrow */}
                            <div className='flex px-10 sm:px-20'>
                                {page.location && !page.partnership && (
                                    <p className='type-style-7  bg-black text-white p-10 mb-30 -ml-10 md:ml-0'>
                                        {page.location}
                                    </p>
                                )}

                                {page.partnership && (
                                    <>
                                        <p className='type-style-7  bg-black text-white p-10 mb-30 -ml-10 md:ml-0 mr-5'>
                                            STRAVA | {page.partnership}
                                        </p>
                                        <p className='type-style-7  bg-black text-white p-10 mb-30'>
                                            Paid Partnership
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className='md:pr-100 px-10 sm:px-20'>
                                <h1 className='type-style-2 text-white inline mr-10'>{page?.title}</h1>

                                {/* Share */}
                                <div className='block md:inline-block align-text-bottom mt-10 md:mt-0'>
                                    <ArticleShareButton article={page} />
                                </div>
                            </div>

                            <div className='mt-30 flex flex-wrap items-center w-full'>
                                {page.activities?.items?.length > 0 && (
                                    <div className='w-full sm:w-2/12 flex items-center px-10 sm:px-20'>
                                        <div className='mr-5'>
                                            <img
                                                alt={page.activities.items?.[0].icon.alt}
                                                className='pointer-events-none'
                                                height='23'
                                                loading='lazy'
                                                role='presentation'
                                                src={page.activities.items?.[0].icon.src}
                                                width='23' />
                                        </div>

                                        <p className='inline-block type-style-7  text-orange'>
                                            {page.activities.items?.[0]?.title}
                                        </p>
                                    </div>
                                )}

                                <div className='flex mt-10 sm:mt-0 px-10 sm:px-20'>
                                    {page.publicationDate && (
                                        <time
                                            className='inline-block type-style-7  text-grey'
                                            dateTime='2017-10-30'
                                            itemProp='datePublished'>
                                            {new Date(page.publicationDate)
                                                .toLocaleDateString('en-us', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })
                                            }
                                        </time>
                                    )}

                                    {page.publicationDate && page.author && (
                                        <p className='inline-block type-style-7  text-grey'>
                                            {page.publicationDate && page.author ? ', by ' : ''}
                                            <a href={`/profiles/${page.author.slug}`}>{page.author && `${page.author.name}`}</a>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Hero image (featured) */}
                    {page.heroImage?.image && !page.displayHeroImageWhole && (
                        <figure className='mt-30 max-w-full'>
                            <div className='left-1/2 -translate-x-1/2 relative w-full px-10 '>
                                <div
                                    className='min-h-[200px] sm:min-h-[422px] md:min-h-[475px] w-full bg-contain bg-repeat-x'
                                    ref={heroRef}
                                    style={{backgroundImage: `url(${page.heroImage.image?.src})`}}
                                    title={page.heroImage.image?.title} />
                            </div>

                            {!!page.heroImage.caption && (
                                <figcaption className='layout-container'>
                                    <div className='md:absolute w-full sm:ml-w-1/6 md:ml-0 sm:w-4/6 md:w-4/12 type-style-7 text-grey whitespace-pre-wrap mt-5 sm:mt-15 px-10 sm:px-20'>
                                        {page.heroImage.caption}
                                    </div>
                                </figcaption>
                            )}
                        </figure>
                    )}

                    {/* Hero image */}
                    {page.heroImage?.image && page.displayHeroImageWhole && (
                        <figure className='mt-30 max-w-full'>
                            <div className='aspect-[5/2]'>
                                <div
                                    className='h-full left-1/2 -translate-x-1/2 relative w-full px-10'
                                    ref={heroRef}>
                                    <Image
                                        {...page.heroImage.image}
                                        className='w-full h-full object-cover'
                                        containerClassName='w-full h-full'
                                        height='100%'
                                        loadWidth={2560}
                                        width='100%' />
                                </div>
                            </div>

                            {!!page.heroImage.caption && (
                                <figcaption className='layout-container'>
                                    <div className='md:absolute w-full sm:ml-w-1/6 md:ml-0 sm:w-4/6 md:w-4/12 type-style-7 text-grey whitespace-pre-wrap mt-5 sm:mt-15 px-10 sm:px-20'>
                                        {page.heroImage.caption}
                                    </div>
                                </figcaption>
                            )}
                        </figure>
                    )}

                    <div className='layout-container'>
                        {/* Content */}
                        {(page.modules?.items?.length > 0) && (
                            <section className='mt-40 sm:mt-50 md:mt-60'>
                                {page.modules.items.map((module, i) => {
                                    return (
                                        <ModuleDispatcher
                                            awardsPage={true}
                                            key={i}
                                            module={module}
                                            nextTypename={page.modules.items[i + 1]?.__typename}
                                            previousTypename={page.modules.items[i - 1]?.__typename} />
                                    );
                                })}
                            </section>
                        )}

                        {/* Editorialist */}
                        {page.author && (
                            <div className='mt-40 sm:mt-50 md:mt-60'>
                                <Profile {...page.author}/>
                            </div>
                        )}
                    </div>
                </article>

                <div className='layout-container'>
                    {tags.length > 0 && (
                        <section className='mt-50 px-10 sm:px-20'>
                            <h2 className='type-style-2 text-white inline mr-10'>
                                Related Tags
                            </h2>

                            <div className='flex flex-wrap mt-30'>
                                {tags.map((tag, idx) => {
                                    if (!tag) {return null;}
                                    return (
                                        <Button
                                            as='a'
                                            className='flex items-center bg-white text-orange rounded type-style-7  px-20 py-15 mr-5'
                                            href={`${tag.__typename === 'BvaMagazineSportPage' ? '/activities/' : tag.__typename === 'BvaMagazineTopicPage' ? '/topics/' : ''}${tag.slug}`}
                                            key={`related-tag-${tag?.title}-${idx}`}
                                            type='button'>
                                            {tag?.title}
                                        </Button>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {data.relatedArticles?.items?.length > 0 && (
                        <section className='mt-50 md:mt-70'>
                            <h2 className='type-style-2 inline mr-10 px-10 sm:px-20'>
                                {t('More Stories')}
                            </h2>

                            <div className='hidden sm:flex mt-30'>
                                {data.relatedArticles.items.map((item, i) => item ? (
                                    <article
                                        aria-label={item.title}
                                        aria-posinset={i + 1}
                                        className={'flex w-full sm:w-4/12 px-10 sm:px-20'}
                                        key={i}
                                        tabIndex={-1}>
                                        <ArticleStandardListingItem {...item} />
                                    </article>
                                ) : null )}
                            </div>

                            <div className='overflow-hidden w-screen max-w-screen left-1/2 relative -translate-x-1/2 sm:hidden'>
                                <CarouselProvider>
                                    <Carousel className='flex swiper-wrapper h-auto w-full image-carousel feature-section'>
                                        {data.relatedArticles.items.map((item, i) => {
                                            if (!item) {return null;}
                                            return (
                                                <CarouselSlide
                                                    className='h-auto flex flex-wrap image-carousel-slide px-10'
                                                    index={i}
                                                    key={`${page.title}-related-article-${i}`}>
                                                    <article
                                                        aria-label={item.title}
                                                        aria-posinset={i + 1}
                                                        className='w-full'
                                                        tabIndex={-1}>
                                                        <ArticleStandardListingItem {...item} />
                                                    </article>
                                                </CarouselSlide>
                                            );})}
                                    </Carousel>
                                </CarouselProvider>
                            </div>
                        </section>
                    )}

                    <div className='border-b border-white mt-60 md:mt-80 mx-10 sm:mx-20' />

                    <Footer
                        domain={domain}
                        invertTextColour={true}
                        menu={data?.footer}
                        title={page.title} />
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    data: PropTypes.shape({
        relatedArticles: PropTypes.shape({
            items: PropTypes.array,
        }),
        page: PropTypes.shape({
            title: PropTypes.string,
            modules: PropTypes.shape({
                items: PropTypes.array
            })
        }),
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        navigation: PropTypes.object,
        search: PropTypes.object,
    }),
    domain: PropTypes.string,
};

AwardsArticlePage.propTypes = propTypes;

export default AwardsArticlePage;
